import { Button, TextField, Tooltip, Form, Box, Select } from '@shopify/polaris';
import React from 'react';
import { useTable, usePagination } from 'react-table';
import { ChevronUpIcon, PlusCircleIcon, DeleteIcon, ExternalSmallIcon, PlusIcon } from '@shopify/polaris-icons';
import ProductSearcherPopover from './productSearcherPopover';
import { addEmptyLine, addLineByIndex, removeLineByIndex, shiftLineUpIndex } from '../util/hanldesave';
import AiTextProcessingModal from './modals/aiTextProcessingModal';

export default function LinesTable({
  id,
  data,
  updateMyData,
  skipPageReset,
  type,
  onSubmit,
  setLines,
  setLinesDirty,
  lineInterface,
  resource,
  company,
  disabled,
  setIsBlurred,
  enableAiTextProcessing = false,
  onProductsAdded // optional: called with the product ids put on a line (e.g. to offer the part documents)
}) {
  const [aiModalActive, setAiModalActive] = React.useState(false);
  const [isProcessingText, setIsProcessingText] = React.useState(false);
  // the editable cells are created once by react-table, so they call the latest callback through a ref
  const onProductsAddedRef = React.useRef(onProductsAdded);
  onProductsAddedRef.current = onProductsAdded;
  const notifyProductsAdded = (productIds) => onProductsAddedRef.current && onProductsAddedRef.current(productIds);

  // Discount codes for Peppol
  const discountCodes = [
    { value: '41', label: '41 - Bonus for works ahead of schedule' },
    { value: '42', label: '42 - Other bonus' },
    { value: '60', label: "60 - Manufacturer's consumer discount" },
    { value: '62', label: '62 - Due to military status' },
    { value: '63', label: '63 - Due to work accident' },
    { value: '64', label: '64 - Special agreement' },
    { value: '65', label: '65 - Production error discount' },
    { value: '66', label: '66 - New outlet discount' },
    { value: '67', label: '67 - Sample discount' },
    { value: '68', label: '68 - End-of-range discount' },
    { value: '70', label: '70 - Incoterm discount' },
    { value: '71', label: '71 - Point of sales threshold allowance' },
    { value: '88', label: '88 - Material surcharge/deduction' },
    { value: '95', label: '95 - Discount' },
    { value: '100', label: '100 - Special rebate' },
    { value: '102', label: '102 - Fixed long term' },
    { value: '103', label: '103 - Temporary' },
    { value: '104', label: '104 - Standard' },
    { value: '105', label: '105 - Yearly turnover' }
  ];

  const handleProcessText = React.useCallback(
    async (text) => {
      setIsProcessingText(true);
      try {
        const response = await fetch('/api/openai/process-text-to-lines', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text,
            lineType: type,
            company
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.details || errorData.error || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.lines && result.lines.length > 0) {
          // Convert AI response to proper line format using lineInterface
          const processedLines = result.lines.map((aiLine) => {
            const newLine = { ...lineInterface };

            // Map AI response fields to line structure
            Object.keys(aiLine).forEach((key) => {
              if (aiLine[key] !== null && aiLine[key] !== undefined) {
                newLine[key] = aiLine[key];
              }
            });

            return newLine;
          });

          // Add the processed lines to the existing lines
          setLines((oldLines) => [...oldLines, ...processedLines]);
          setLinesDirty(true);

          // Show success message (you can implement a toast notification here)
          console.log(`Successfully processed ${processedLines.length} lines from text`);
        } else {
          throw new Error('No lines were generated from the text. Please check your input and try again.');
        }
      } catch (error) {
        console.error('Error processing text with AI:', error);

        // Show user-friendly error message
        let errorMessage = 'Failed to process text with AI. ';

        if (error.message.includes('timeout')) {
          errorMessage += 'The request took too long. Please try with shorter text or try again.';
        } else if (error.message.includes('AI service unavailable')) {
          errorMessage += 'AI service is not configured. Please contact your administrator.';
        } else if (error.message.includes('No lines were generated')) {
          errorMessage += error.message;
        } else {
          errorMessage += 'Please check your input and try again.';
        }

        // You can implement a toast notification here to show the error message
        alert(errorMessage); // Replace with proper toast notification
      } finally {
        setIsProcessingText(false);
      }
    },
    [type, company, lineInterface, setLines, setLinesDirty]
  );

  const columns = React.useMemo(() => {
    let cols =
      type === 'non-financial'
        ? [
            {
              Header: 'Code',
              accessor: 'produktnr',
              type: 'number',
              prefix: '#',
              weight: 8
            },
            {
              Header: 'Description',
              accessor: 'tekstlijn',
              type: 'text',
              weight: 55
            },
            {
              Header: 'Ordered',
              accessor: 'aantalbesteld',
              type: 'number',
              prefix: '#',
              weight: 8
            },
            {
              Header: 'Delivered',
              accessor: 'aantalgeleverd',
              type: 'number',
              prefix: '#',
              weight: 8
            }
          ]
        : type === 'financial-simple'
        ? [
            {
              Header: 'Qty',
              accessor: 'aantal',
              type: 'number',
              weight: 5
            },
            {
              Header: 'Code',
              accessor: 'produktnr',
              type: 'number',
              weight: 10
            },
            {
              Header: 'Description',
              accessor: 'tekstlijn',
              type: 'text',
              weight: 25
            },
            {
              Header: 'VP',
              accessor: 'VP',
              type: 'currency',
              prefix: '€',
              weight: 8
            },
            {
              Header: 'VAT',
              accessor: 'BTW',
              type: 'number',
              prefix: '%',
              weight: 8
            },
            {
              Header: 'Discount Code',
              accessor: 'DiscountCode',
              type: 'select',
              weight: 5
            }
          ]
        : [
            {
              Header: 'Qty',
              accessor: 'aantal',
              type: 'number',
              weight: 8
            },
            {
              Header: 'Code',
              accessor: 'produktnr',
              type: 'number',
              weight: 10
            },
            {
              Header: 'Description',
              accessor: 'tekstlijn',
              type: 'text',
              weight: 25
            },
            {
              Header: 'AP',
              accessor: 'AP',
              type: 'currency',
              prefix: '€',
              weight: 8
            },
            {
              Header: 'Date AP',
              accessor: 'Datum bijw_AP',
              type: 'date',
              weight: 4
            },
            {
              Header: 'VP',
              accessor: 'VP',
              type: 'currency',
              prefix: '€',
              weight: 8
            },
            {
              Header: 'Date VP',
              accessor: 'Datum  bijw_ VP',
              type: 'date',
              weight: 4
            },
            {
              Header: 'VAT',
              accessor: 'BTW',
              type: 'number',
              prefix: '%',
              weight: 4
            },
            {
              Header: 'Discount Code',
              accessor: 'DiscountCode',
              type: 'select',
              weight: 5
            }
          ];

    return cols;
  }, [type]);

  // Create an editable cell renderer
  const EditableCell = ({
    value: initialValue,
    row,
    column: { id, type, prefix },
    updateMyData, // This is a custom function that we supplied to our table instance
    resource,
    company,
    disabled,
    lineInterface
  }) => {
    // We need to keep and update the state of the cell normally
    const [value, setValue] = React.useState(initialValue);

    const onChange = (e) => {
      setIsBlurred(false);
      setLinesDirty(true);
      if (id === 'produktnr') {
        e.match(/^[0-9]*$/) //produktnr can only accept numbers or blank
          ? setValue(parseInt(e))
          : null;
      } else {
        setValue(e);
      }
      if (id !== 'produktnr') {
        updateMyData(row.index, id, e, setLines, setLinesDirty, resource, company, setIsBlurred);
      }
    };

    const onChangePopover = async (e) => {
      // Handle both single product ID and array of product IDs
      if (Array.isArray(e)) {
        // Multiple products selected
        if (e.length > 0) {
          // Update current line with first product
          setValue(e[0]);
          updateMyData(row.index, id, e[0], setLines, setLinesDirty, resource, company, setIsBlurred);
          notifyProductsAdded(e);

          // Add new lines for remaining products
          if (e.length > 1) {
            // Fetch product details for all additional products
            const productDetailsPromises = e.slice(1).map(async (productId) => {
              try {
                const response = await fetch(`/api/products/${productId}`);
                if (response.ok) {
                  return await response.json();
                }
                return null;
              } catch (error) {
                console.error(`Error fetching product ${productId}:`, error);
                return null;
              }
            });

            const productsData = await Promise.all(productDetailsPromises);

            // Prepare text based on language
            const prepareTextLine = (data) => {
              if (!data) return '';
              switch (parseInt(resource.TaalID)) {
                case 1:
                  return data.ProductNameNL;
                case 2:
                  return data.ProductNameFR;
                case 3:
                  return data.ProductNameUK;
                case 4:
                  return data.ProductNameDE;
                default:
                  return data.ProductNameNL;
              }
            };

            // Create new lines with full product data
            setLines((old) => {
              const newLines = [...old];
              productsData.forEach((data, index) => {
                if (data) {
                  const newLine = {
                    ...lineInterface,
                    produkthulpnr: data.ProductID,
                    ProductID: data.ProductID,
                    ProductName: data.name,
                    ProductNameNL: data.name_nl,
                    ProductNameFR: data.name_fr,
                    Productref: data.Productref,
                    AP: data.Eenheidsaankoopprijs,
                    VP: data['Eenheids verkoopprijs'],
                    'Datum  bijw_ VP': data['Datum  bijw_ VP'],
                    'Datum bijw_AP': data['Datum bijw_AP'],
                    tekstlijn: prepareTextLine(data),
                    BTW: parseInt(company.LandID) === 300 ? 21 : 0,
                    aantal: 1,
                    Eenheid: data.EenheidID,
                    ProductNameDE: data.ProductNameDE,
                    ProductNameUK: data.ProductNameUK,
                    EenheidID: data.unit_id,
                    EenheidAantal: data.EenheidAantal,
                    ParentProductID: data.parent_product_id,
                    HasDetails: data.HasDetails,
                    HSCode: data.HSCode,
                    Kg: data.Kg,
                    OnderdelenTypeID: data.OnderdelenTypeID,
                    Discontinued: data.Discontinued,
                    produktnr: data.ProductID,
                    notExisting: false
                  };
                  newLines.splice(row.index + index + 1, 0, newLine);
                }
              });
              return newLines;
            });
            setLinesDirty(true);
          }
        }
      } else {
        // Single product ID (existing behavior)
        setValue(e);
        updateMyData(row.index, id, e, setLines, setLinesDirty, resource, company, setIsBlurred);
        notifyProductsAdded([e]);
      }
    };

    const onChangeSelect = (e) => {
      setValue(e);
      updateMyData(row.index, id, e, setLines, setLinesDirty, resource, company, setIsBlurred);
    };

    // We'll only update the external data when the input is blurred
    const onBlur = () => {
      updateMyData(row.index, id, value, setLines, setLinesDirty, resource, company, setIsBlurred);
      if (id === 'produktnr' && value && value !== initialValue) notifyProductsAdded([value]);
    };

    // If the initialValue is changed external, sync it up with our state
    React.useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const detectError = () => {
      if (id === 'produktnr') {
        if (row.original?.notExisting) return 'Product does not exist';
        if (row.original?.Discontinued) return 'Product discontinued';
      }
      // Check if line is negative and discount code is missing
      if (id === 'DiscountCode') {
        const lineTotal = (parseFloat(row.original?.VP) || 0) * (parseFloat(row.original?.aantal) || 0);
        if (lineTotal < 0 && !value) {
          return 'Discount code is required for negative lines';
        }
      }
      // Check if VP > 0 and VAT is negative
      if (id === 'BTW') {
        const vp = parseFloat(row.original?.VP) || 0;
        const vat = parseFloat(value) || 0;
        const isBelgium = parseInt(company?.LandID) === 300;

        if (isBelgium) {
          if (vp > 0 && vat < 0) {
            return 'Not allowed';
          }
        } else {
          if (vat !== 0) {
            return 'Must be 0';
          }
        }
      }
      return false;
    };
    const textFieldError = detectError();

    const detectWarning = () => {
      if (id === 'BTW') {
        const vp = parseFloat(row.original?.VP) || 0;
        const vat = parseFloat(value) || 0;
        const isBelgium = parseInt(company?.LandID) === 300;
        if (vp > 0 && vat > 0 && vat !== 21) {
          return 'Check VAT rate';
        }
        if (isBelgium && vp > 0 && vat === 0) {
          return 'Sent via Peppol as zero rated (VAT category Z)';
        }
      }
      return false;
    };
    const textFieldWarning = detectWarning();

    // Handle select type (for discount codes)
    if (type === 'select' && id === 'DiscountCode') {
      const lineTotal = (parseFloat(row.original?.VP) || 0) * (parseFloat(row.original?.aantal) || 0);
      const isNegative = lineTotal < 0;

      return (
        <Select
          id="discountCode"
          options={[{ value: '', label: 'Select discount code' }, ...discountCodes]}
          onChange={onChangeSelect}
          value={value || ''}
          disabled={resource.Verstuurd || disabled || !isNegative}
          error={isNegative && !value ? 'Required for negative lines' : undefined}
        />
      );
    }

    return (
      <TextField
        id="line"
        type={type === 'number' || type === 'currency' ? 'currency' : 'text'}
        value={value ? (type === 'date' ? String(value).substring(0, 10) : String(value)) : ''}
        onChange={onChange}
        onBlur={onBlur}
        prefix={prefix}
        disabled={resource.Verstuurd ? true : type === 'date' ? true : disabled ? true : false}
        error={textFieldError}
        helpText={textFieldWarning}
        autoComplete="off"
        connectedRight={id === 'produktnr' ? <ProductSearcherPopover value={value} handleChange={onChangePopover} disabled={disabled || resource.Verstuurd} /> : null}
        maxLength={100} //restrict tesklijn values to be longer than 100 chars
      />
    );
  };

  // Set our editable cell renderer as the default Cell renderer
  const defaultColumn = {
    Cell: EditableCell
  };

  // For this example, we're using pagination to illustrate how to stop
  // the current page from resetting when our data changes
  // Otherwise, nothing is different here.
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns,
      data,
      defaultColumn,
      autoResetPage: !skipPageReset,
      // use the skipPageReset option to disable page resetting temporarily
      // updateMyData isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      updateMyData,
      resource,
      company,
      disabled,
      lineInterface
    },
    usePagination
  );

  return (
    <>
      <Form onSubmit={onSubmit} id="linesTable" autoComplete="off">
        {/* AI Text Processing Button */}
        {enableAiTextProcessing && (
          <Box paddingBlockEnd="300">
            <Button size="large" onClick={() => setAiModalActive(true)} disabled={disabled} primary>
              Process Text with AI
            </Button>
          </Box>
        )}

        <table {...getTableProps()} style={{ width: '100%', marginTop: '0px' }}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} style={{ width: column.weight + '%', textAlign: 'left', fontWeight: 400, fontSize: 15, paddingBottom: '5px' }}>
                    {column.render('Header')}
                  </th>
                ))}
                <th style={{ width: '2%' }}></th>
                <th style={{ width: '5%' }}></th>
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <tr id="lineRow" {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} style={{ verticalAlign: 'top' }}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}

                  <td style={{ verticalAlign: 'top' }}>
                    <Tooltip content="Open part in new tab">
                      <Button
                        size="large"
                        icon={ExternalSmallIcon}
                        disabled={disabled}
                        style={{ margin: '-5px' }}
                        onClick={() => window.open(`/products/${row.cells.filter((cell) => cell.column.id === 'produktnr')[0].value}`, '_blank').focus()}
                      />
                    </Tooltip>
                  </td>

                  <td style={{ verticalAlign: 'top' }}>
                    {id === 'new' && i > 0 && (
                      <Tooltip content="Move line up">
                        <Button size="large" disabled={disabled} icon={ChevronUpIcon} onClick={() => shiftLineUpIndex(setLines, setLinesDirty, i)} />
                      </Tooltip>
                    )}
                    {id === 'new' && (
                      <Tooltip content="Add line above this line.">
                        <Button size="large" disabled={disabled} icon={PlusIcon} onClick={() => addLineByIndex(setLines, setLinesDirty, lineInterface, i)} />
                      </Tooltip>
                    )}
                    <Tooltip content="Remove line">
                      <Button size="large" disabled={disabled} onClick={() => removeLineByIndex(setLines, setLinesDirty, i, setIsBlurred)} icon={DeleteIcon} />
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <Box paddingBlockStart="300">
          <Tooltip content="Add a line to the table">
            <Button size="large" disabled={disabled} id="addLine" onClick={() => addEmptyLine(setLines, setLinesDirty, lineInterface)} icon={PlusCircleIcon} />
          </Tooltip>
        </Box>
      </Form>

      {/* AI Text Processing Modal */}
      <AiTextProcessingModal active={aiModalActive} setActive={setAiModalActive} onProcessText={handleProcessText} isProcessing={isProcessingText} />
    </>
  );
}
